// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Draws one frame of the logo against a `Painter` — the slice of the canvas
 * 2D context it uses, so a test can count what was drawn without a DOM.
 *
 * At rest (`m = 0`) the frame is static: the letters and stroke A solid in
 * the ink colour, stroke B as accent points. While playing, stroke A fades
 * into its own points as they start to move (one motion), and every point
 * travels, arcing, to its place in the part, tinting from its colour towards
 * the part's.
 */

import { rgbaString, mixRgba, type Blend, type Rgba } from './color';
import { clamp, strokeAPolygon, WORD_TEXT, type WordLayout, type XGeometry } from './geometry';
import { P_ALPHA, P_GAUSS, P_KIND, P_ORDER, P_X, P_Y, STRIDE, type LogoModel } from './points';

/** The canvas 2D calls `drawFrame` makes; `CanvasRenderingContext2D` satisfies it. */
export interface Painter {
  globalCompositeOperation: string;
  fillStyle: unknown;
  font: string;
  textBaseline: string;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
  fill(): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
}

export interface FrameState {
  /** Morph, 0 (X) … 1 (part). */
  m: number;
  /** Index into `PARTS` / `model.targets`. */
  part: number;
}

export interface LogoPalette {
  /** Letters, stroke A and its points: the element's `color`. */
  ink: Rgba;
  /** Stroke B's points. */
  accent: Rgba;
  /** Per part (in `PARTS` order): the colour its points tint to. */
  partColors: readonly Rgba[];
  /** The brand font family list (`--font-brand`). */
  font: string;
}

/** The brand face's weight. */
export const BRAND_WEIGHT = 600;

const ease3 = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

function dot(p: Painter, x: number, y: number, r: number): void {
  if (r < 1) p.fillRect(x - r, y - r, 2 * r, 2 * r);
  else {
    p.beginPath();
    p.arc(x, y, r, 0, Math.PI * 2);
    p.fill();
  }
}

export function drawFrame(
  p: Painter,
  model: LogoModel,
  layout: WordLayout,
  g: XGeometry,
  state: FrameState,
  palette: LogoPalette,
  blend: Blend,
  dpr = 1,
): void {
  const m = clamp(state.m, 0, 1);
  // Stroke A is gone by 40 % of the morph.
  const dd = clamp(m * 2.5, 0, 1);
  const { it, N, rad } = model;
  const target = model.targets[state.part] ?? model.targets[0];
  const partColor = palette.partColors[state.part] ?? palette.accent;

  p.setTransform(dpr, 0, 0, dpr, 0, 0);
  p.globalCompositeOperation = 'source-over';
  p.clearRect(0, 0, layout.W, layout.H);
  p.font = `${BRAND_WEIGHT} ${layout.F}px ${palette.font}`;
  p.textBaseline = 'alphabetic';
  p.fillStyle = rgbaString(palette.ink);
  if (layout.hasText) p.fillText(WORD_TEXT, layout.side, layout.base);
  if (dd < 1) {
    p.fillStyle = rgbaString(palette.ink, 1 - dd);
    const [first, ...rest] = strokeAPolygon(layout.X, g);
    p.beginPath();
    p.moveTo(first[0], first[1]);
    for (const [x, y] of rest) p.lineTo(x, y);
    p.closePath();
    p.fill();
  }

  p.globalCompositeOperation = blend;
  for (let i = 0; i < it.length; i += STRIDE) {
    const kind = it[i + P_KIND];
    let x = it[i + P_X];
    let y = it[i + P_Y];
    let a = it[i + P_ALPHA] * 0.9;
    let col = kind === 2 ? palette.accent : palette.ink;
    if (kind === 1) {
      a *= dd;
      if (a < 0.01) continue;
    }
    if (m > 0 && target) {
      const ti = it[i + P_ORDER];
      // Points leave left to right: the leftmost first, the rightmost 45 % of the morph later.
      const e = ease3(clamp(m * 1.45 - (ti / N) * 0.45, 0, 1));
      const arc = Math.sin(Math.PI * e);
      x += (target[2 * ti] - x) * e;
      y += (target[2 * ti + 1] - y) * e + arc * it[i + P_GAUSS] * layout.cap * 0.06;
      col = mixRgba(col, partColor, e);
      a = Math.min(1, a + arc * 0.4);
    }
    p.fillStyle = rgbaString(col, a);
    dot(p, x, y, rad);
  }
  p.globalCompositeOperation = 'source-over';
}
